<template>
    <div class="col-span-12">
        <div class="playground-nav mb-lg flex flex-wrap items-center gap-sm">
            <button
                type="button"
                class="playground-nav-button"
                :class="{ 'playground-nav-button--active': selectedGroupId === null }"
                @click="showAllGroups"
            >
                All Groups
            </button>
            <button
                v-for="group in playgroundGroups"
                :key="group.id"
                type="button"
                class="playground-nav-button"
                :class="{ 'playground-nav-button--active': isSelectedGroup(group.id) }"
                @click="selectGroup(group.id)"
            >
                {{ group.name }}
            </button>
        </div>
        <p class="mb-xl text-sm text-gray-300">
            Click any group header to isolate it. Click the same header again or use the navigation above to return.
        </p>

        <div v-if="isGroupVisible('input-select')" id="component-group">
            <button type="button" class="component-group-header component-group-toggle col-span-12 pb-4" @click="selectGroup('input-select')">
                <h1>Input-select</h1>
            </button>
            <div class="component-group-list gap-lg col-span-12 flex flex-row flex-wrap">
                <PlaygroundInputSelectGroup />
            </div>
        </div>

        <div v-if="isGroupVisible('tag-cell')" id="component-group">
            <button type="button" class="component-group-header component-group-toggle col-span-12 pb-4" @click="selectGroup('tag-cell')">
                <h1>TagCell</h1>
            </button>
            <div class="component-group-list gap-lg col-span-12 flex flex-row flex-wrap">
                <div
                    v-for="value in [
                        'default',
                        '',
                        'success',
                        'warning',
                        'danger',
                        'info',
                        'hold',
                        'done',
                        'wip',
                    ] as TagVariant[]"
                    :key="value"
                >
                    <TagComponent :variant="value" :value="value" />
                </div>
            </div>
        </div>

        <div v-if="isGroupVisible('tag-status')" id="component-group">
            <button type="button" class="component-group-header component-group-toggle col-span-12 pb-4" @click="selectGroup('tag-status')">
                <h1>TagComponent (Status Variants)</h1>
            </button>
            <div class="component-group-list gap-lg col-span-12 flex flex-row flex-wrap">
                <div
                    v-for="value in [
                        'Done',
                        'Resolved',
                        'Cancelled',
                        'Closed',
                        'In progress',
                        'IN PROGRESS',
                        'APPROVED',
                        'WAITING FOR APPROVAL',
                        'Review',
                        'Summary',
                        'Technical',
                        'Billing',
                    ]"
                    :key="value"
                >
                    <TagComponent :value="value" />
                </div>
            </div>
        </div>

        <div v-if="isGroupVisible('response-time-cell')" id="component-group">
            <button
                type="button"
                class="component-group-header component-group-toggle col-span-12 pb-4"
                @click="selectGroup('response-time-cell')"
            >
                <h1>ResponseTimeCell</h1>
            </button>
            <div class="component-group-list gap-lg col-span-12 flex flex-row flex-wrap">
                <div v-for="value in [-100, 2600000, 86400001]" :key="value">
                    <ResponseTimeCell :response-time="value" />
                </div>
            </div>
        </div>

        <div v-if="isGroupVisible('data-table-mount')" id="component-group">
            <button
                type="button"
                class="component-group-header component-group-toggle col-span-12 pb-4"
                @click="selectGroup('data-table-mount')"
            >
                <h1>DataTable Mount</h1>
            </button>
            <div class="component-group-list gap-lg col-span-12 flex flex-row flex-wrap">
                <div>
                    <button @click="toggleDataTable">Toggle mount</button>
                </div>
                <div v-if="showDataTable">
                    <div v-for="value in ['x']" :key="value">
                        <DataTablePlayground />
                    </div>
                </div>
            </div>
        </div>

        <div v-if="isGroupVisible('data-table')" id="component-group">
            <button type="button" class="component-group-header component-group-toggle col-span-12 pb-4" @click="selectGroup('data-table')">
                <h1>DataTable</h1>
            </button>
            <div class="component-group-list gap-lg flex w-full flex-row flex-wrap">
                <PlaygroundDataTable />
            </div>
        </div>

        <div v-if="isGroupVisible('incident-type-icon')" id="component-group">
            <button
                type="button"
                class="component-group-header component-group-toggle col-span-12 pb-4"
                @click="selectGroup('incident-type-icon')"
            >
                <h1>IncidentTypeIcon</h1>
            </button>
            <div class="component-group-list gap-lg col-span-12 flex flex-row flex-wrap">
                <div class="flex items-center gap-sm">
                    <IncidentTypeIcon
                        class="h-6 w-6 text-blue-500"
                        icon-key="waste-management"
                        type-title="Waste Management"
                    />
                    <span>Waste Management</span>
                </div>
            </div>
        </div>

        <div v-if="isGroupVisible('jira-wiki-preview')" id="component-group">
            <button
                type="button"
                class="component-group-header component-group-toggle col-span-12 pb-4"
                @click="selectGroup('jira-wiki-preview')"
            >
                <h1>Jira Wiki Preview</h1>
            </button>
            <PlaygroundJiraWikiPreviewGroup />
        </div>

        <div v-if="isGroupVisible('adf-preview')" id="component-group">
            <button
                type="button"
                class="component-group-header component-group-toggle col-span-12 pb-4"
                @click="selectGroup('adf-preview')"
            >
                <h1>ADF Preview</h1>
            </button>
            <PlaygroundAdfPreviewGroup />
        </div>

        <div v-if="isGroupVisible('comment-editor')" id="component-group">
            <button type="button" class="component-group-header component-group-toggle col-span-12 pb-4" @click="selectGroup('comment-editor')">
                <h1>CommentEditor</h1>
            </button>
            <PlaygroundCommentEditorGroup />
        </div>
    </div>
</template>

<script setup lang="ts">
// TODO: nice to have update handler for all the selects
import PlaygroundInputSelectGroup from '@/views/playground-input-select-group.vue';
import PlaygroundAdfPreviewGroup from '@/views/playground/playground-adf-preview-group.vue';
import PlaygroundJiraWikiPreviewGroup from '@/views/playground/playground-jira-wiki-preview-group.vue';
import DataTablePlayground from './playground-data-table.vue';
import ResponseTimeCell from '@/components/tables/cells/ResponseTimeCell.vue';
import { ref } from 'vue';
import TagComponent from '@/components/ui/tag/TagComponent.vue';
import type { TagVariant } from '@/components/tables/types';
import PlaygroundDataTable from './playground-data-table.vue';
import IncidentTypeIcon from '@/components/incident/IncidentTypeIcon.vue';
import PlaygroundCommentEditorGroup from '@/views/playground/playground-comment-editor-group.vue';

const playgroundGroups = [
    { id: 'input-select', name: 'Input-select' },
    { id: 'tag-cell', name: 'TagCell' },
    { id: 'tag-status', name: 'TagComponent (Status Variants)' },
    { id: 'response-time-cell', name: 'ResponseTimeCell' },
    { id: 'data-table-mount', name: 'DataTable Mount' },
    { id: 'data-table', name: 'DataTable' },
    { id: 'incident-type-icon', name: 'IncidentTypeIcon' },
    { id: 'jira-wiki-preview', name: 'Jira Wiki Preview' },
    { id: 'adf-preview', name: 'ADF Preview' },
    { id: 'comment-editor', name: 'CommentEditor' },
] as const;

type PlaygroundGroupId = (typeof playgroundGroups)[number]['id'];

const showDataTable = ref<boolean>(true);
const selectedGroupId = ref<PlaygroundGroupId | null>(null);

const isSelectedGroup = (groupId: PlaygroundGroupId) => {
    return selectedGroupId.value === groupId;
};

const isGroupVisible = (groupId: PlaygroundGroupId) => {
    return selectedGroupId.value === null || selectedGroupId.value === groupId;
};

const selectGroup = (groupId: PlaygroundGroupId) => {
    selectedGroupId.value = selectedGroupId.value === groupId ? null : groupId;
};

const showAllGroups = () => {
    selectedGroupId.value = null;
};

const toggleDataTable = () => {
    showDataTable.value = !showDataTable.value;
};

// const token = localStorage.getItem('token');

// onMounted(() => {
//     checkRoutes();
// });

// const checkRoutes = () => {
//     if (!token) {
//         router.push('/login');
//         return;
//     }
// };
</script>

<style scoped>
.custom-a {
    font-family: 'Matter';
    font-weight: 700;
}

.custom-b {
    font-family: 'Matter-Bold';
    font-weight: 700;
}

.playground-nav-button {
    border: 1px solid var(--color-gray-100);
    border-radius: 9999px;
    background: var(--color-white);
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
    line-height: 1.25rem;
    transition:
        border-color 150ms ease,
        color 150ms ease,
        background-color 150ms ease;
}

.playground-nav-button:hover,
.component-group-toggle:hover {
    border-color: var(--color-blue-500);
    color: var(--color-blue-500);
}

.playground-nav-button--active {
    border-color: var(--color-blue-500);
    background: var(--color-blue-50);
    color: var(--color-blue-500);
}

.component-group-toggle {
    display: flex;
    width: 100%;
    cursor: pointer;
    border: 0;
    background: transparent;
    padding-inline: 0;
    text-align: left;
}
</style>
